import { authenticatedFetch } from "@/api/session";
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ??
  "http://localhost:8000";

export type PilotRequestStatus =
  | "SUBMITTED"
  | "UNDER_REVIEW"
  | "APPROVED"
  | "DECLINED"
  | "CONVERTED";

export type PilotRequestSource =
  | "WEBSITE"
  | "REFERRAL"
  | "MANUAL"
  | string;

export type PilotRequest = {
  id: string;
  church_id?: string | null;
  church_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string | null;
  church_size?: string | null;
  current_system?: string | null;
  primary_challenge?: string | null;
  notes?: string | null;
  internal_notes?: string | null;
  source: PilotRequestSource;
  status: PilotRequestStatus;
  submitted_at?: string | null;
  reviewed_at?: string | null;
  approved_at?: string | null;
  converted_at?: string | null;
  created_at: string;
  updated_at: string;
};

export type PilotRequestListResponse = {
  items: PilotRequest[];
  total: number;
  offset: number;
  limit: number;
};

export type PilotRequestStatsResponse = {
  total: number;
  submitted: number;
  under_review: number;
  approved: number;
  declined: number;
  converted: number;
};

export type PlatformAuditEvent = {
  id: string;
  actor_user_id?: string | null;
  actor_type: string;
  church_id?: string | null;
  event_type: string;
  entity_type: string;
  entity_id?: string | null;
  ip_address?: string | null;
  user_agent?: string | null;
  request_id?: string | null;
  event_data?: Record<string, unknown> | null;
  created_at: string;
};

export type PlatformActivityResponse = {
  items: PlatformAuditEvent[];
  total: number;
  offset: number;
  limit: number;
};

export type PlatformDashboardKpis = {
  total_requests: number;
  pending_review: number;
  approved_requests: number;
  converted_requests: number;
  active_churches: number;
  pending_invitations: number;
  churches_onboarding: number;
  imports_running: number;
  average_approval_hours: number | null;
};

export type PlatformDashboardFunnelItem = {
  status: string;
  count: number;
};

export type PlatformDashboardResponse = {
  kpis: PlatformDashboardKpis;
  request_funnel: PlatformDashboardFunnelItem[];
  recent_requests: PilotRequest[];
  recent_activity: PlatformAuditEvent[];
};

export type PilotRequestFilters = {
  search?: string;
  status?: PilotRequestStatus | "ALL";
  offset?: number;
  limit?: number;
};

export type PlatformActivityFilters = {
  church_id?: string;
  event_type?: string;
  entity_type?: string;
  entity_id?: string;
  offset?: number;
  limit?: number;
};

export type UpdatePilotRequestPayload = {
  status?: PilotRequestStatus;
  internal_notes?: string | null;
};

export type ProvisionChurchPayload = {
  church_name: string;
  slug: string;
  timezone: string;

  admin_first_name: string;
  admin_last_name: string;
  admin_email: string;

  max_users: number;
  max_member_records: number;

  pilot_end_date: string;

  invitation_expires_in_days: number;

  feature_flags: Record<string, boolean>;
};

export type ProvisionChurchResponse = {
  pilot_request_id: string;
  pilot_request_status: string;

  church: {
    id: string;
    name: string;
    slug: string;
  };

  invitation: {
    id: string;
    invited_email: string;
    status: string;
  };

  invitation_token: string;
};

export type PlatformChurchAdministrator = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  is_active: boolean;
};

export type PlatformChurchInvitation = {
  id: string;

  invited_email: string;
  invited_first_name?: string | null;
  invited_last_name?: string | null;

  role_code: string;

  status: string;

  send_count: number;

  sent_at?: string | null;
  last_sent_at?: string | null;

  expires_at: string;

  accepted_at?: string | null;
  revoked_at?: string | null;

  created_at: string;
  updated_at: string;
};

export type PlatformChurchSummary = {
  id: string;
  name: string;
  slug: string;
  city?: string | null;
  state?: string | null;

  status: string;
  lifecycle_stage: string;
  activation_status?: string;
  activation_requirement?: string | null;
  administrator_activated?: boolean;
  is_seeded?: boolean;
  member_count?: number;
  household_count?: number;
  service_schedule_count?: number;
  service_occurrence_count?: number;
  attendance_count?: number;
  visitor_count?: number;
  last_activity_at?: string | null;

  plan_code?: string | null;

  max_users: number;
  max_member_records: number;

  pilot_started_at?: string | null;
  pilot_expires_at?: string | null;

  administrator?: PlatformChurchAdministrator | null;

  invitation?: PlatformChurchInvitation | null;
};

export type PlatformChurchListResponse = {
  items: PlatformChurchSummary[];
  total: number;
  offset: number;
  limit: number;
};

export type PlatformChurchDetail = {
  church: PlatformChurchSummary;

  administrators: PlatformChurchAdministrator[];

  invitation?: PlatformChurchInvitation | null;

  pilot_request?: PilotRequest | null;

  recent_activity: PlatformAuditEvent[];
};

export type PlatformChurchFilters = {
  search?: string;

  status?: string;

  lifecycle_stage?: string;

  offset?: number;

  limit?: number;
};

export type SeedChurchSummary = {
  church_id: string;
  households_created: number;
  members_created: number;
  schedules_created: number;
  occurrences_created: number;
  attendance_created: number;
  visitors_created: number;
  care_items_created: number;
};

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem("platform_access_token");

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

async function parseError(response: Response): Promise<string> {
  try {
    const body = await response.json();

    if (typeof body?.detail === "string") {
      return body.detail;
    }

    if (Array.isArray(body?.detail)) {
      return body.detail
        .map((item: { msg?: string }) => item.msg ?? "Validation error")
        .join(", ");
    }
  } catch {
    // Fall back to the default message below.
  }

  if (response.status === 403) return "You do not have permission to perform this action.";
  if (response.status === 404) return "The requested church could not be found.";
  if (response.status === 409) return "This church already contains operational data and cannot be seeded.";
  if (response.status >= 500) return "The operation failed. No partial changes were saved.";
  return `Request failed with status ${response.status}`;
}

function buildQueryString(
  params: Record<string, string | number | undefined>
): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      searchParams.set(key, String(value));
    }
  });

  const query = searchParams.toString();

  return query ? `?${query}` : "";
}

export async function getPlatformDashboard(): Promise<PlatformDashboardResponse> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/platform/dashboard`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  return response.json();
}

export async function getPilotRequests(
  filters: PilotRequestFilters = {}
): Promise<PilotRequestListResponse> {
  const query = buildQueryString({
    search: filters.search,
    status:
      filters.status && filters.status !== "ALL"
        ? filters.status
        : undefined,
    offset: filters.offset ?? 0,
    limit: filters.limit ?? 50,
  });

  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/platform/pilot-requests${query}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  return response.json();
}

export async function getPilotRequestStats(): Promise<PilotRequestStatsResponse> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/platform/pilot-requests/stats`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  return response.json();
}

export async function getPlatformActivity(
  filters: PlatformActivityFilters = {}
): Promise<PlatformActivityResponse> {
  const query = buildQueryString({
    church_id: filters.church_id,
    event_type: filters.event_type,
    entity_type: filters.entity_type,
    entity_id: filters.entity_id,
    offset: filters.offset ?? 0,
    limit: filters.limit ?? 50,
  });

  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/platform/activity${query}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  return response.json();
}

export async function updatePilotRequest(
  requestId: string,
  payload: UpdatePilotRequestPayload
): Promise<PilotRequest> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/platform/pilot-requests/${requestId}`,
    {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  return response.json();
}

export async function provisionPilotChurch(
  requestId: string,
  payload: ProvisionChurchPayload
): Promise<ProvisionChurchResponse> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/platform/pilot-requests/${requestId}/provision`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  return response.json();
}

export async function getPlatformChurches(
  filters: PlatformChurchFilters = {}
): Promise<PlatformChurchListResponse> {
  const query = buildQueryString({
    search: filters.search,
    status: filters.status,
    lifecycle_stage: filters.lifecycle_stage,
    offset: filters.offset ?? 0,
    limit: filters.limit ?? 50,
  });

  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/platform/churches${query}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  return response.json();
}

export async function getPlatformChurch(
  churchId: string
): Promise<PlatformChurchDetail> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/platform/churches/${churchId}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  return response.json();
}


export async function getInvitationActivity(
  invitationId: string,
) {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/platform/activity?entity_type=INVITATION&entity_id=${encodeURIComponent(
      invitationId,
    )}`,
    {
      headers: getAuthHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error(
      await parseError(response),
    );
  }

  return response.json() as Promise<
    PlatformActivityResponse
  >;
}

export async function seedPlatformChurch(
  churchId: string,
): Promise<SeedChurchSummary> {
  let response: Response;
  try {
    response = await authenticatedFetch(
      `${API_BASE_URL}/api/platform/churches/${churchId}/seed`,
      { method: "POST", headers: getAuthHeaders() },
    );
  } catch {
    throw new Error(
      "MinistryIQ could not reach the API. Check your connection and try again.",
    );
  }

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  return response.json();
}


export type PlatformIntelligenceRun = {
  id: string; trigger: string; status: string; started_at: string;
  completed_at: string | null; duration_ms: number | null;
  churches_evaluated: number; care_items_created: number; people_skipped: number;
};
export type PlatformIntelligenceHealth = {
  scheduler_enabled: boolean; scan_interval_minutes: number;
  last_sweep: PlatformIntelligenceRun | null; recent_runs: PlatformIntelligenceRun[];
  churches_succeeded: number | null; churches_failed: number | null;
  members_evaluated: number | null; households_evaluated: number | null;
};
export async function getPlatformIntelligenceHealth(): Promise<PlatformIntelligenceHealth> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/platform/intelligence/health`, {headers: getAuthHeaders()});
  if (!response.ok) throw new Error("Unable to load platform intelligence operations.");
  return response.json();
}
